<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useModal } from '@/composables/useModal'

import AccountChip from '@/components/AccountChip.vue'
import AuthModal from '@/components/AuthModal.vue'
import WelcomeBonus from '@/components/WelcomeBonus.vue'
import GenerationIndicator from '@/components/GenerationIndicator.vue'
import GenerationOverlay from '@/components/GenerationOverlay.vue'
import GenerationToasts from '@/components/GenerationToasts.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import UigIcon from '@/components/UigIcon.vue'
import OnboardingFrame from '@/components/onboarding/OnboardingFrame.vue'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useGenerationsStore } from '@/stores/generations'
import { useWelcomeBonusStore } from '@/stores/welcomeBonus'

const route = useRoute()
const app = useAppStore()
const auth = useAuthStore()
const generations = useGenerationsStore()
const welcomeBonus = useWelcomeBonusStore()

const tabs = [
  { to: '/home', label: 'Home', icon: 'house_fill' },
  { to: '/studio', label: 'AI Studio', icon: 'sparkles' },
  { to: '/profile', label: 'Profile', icon: 'person_fill' },
] as const

const initialPlans = computed(() => route.meta.onboardingPlans === true)
const bareLayout = computed(() => route.meta.layout === 'bare')
const showChrome = computed(() => route.meta.chrome !== false)

function guardUpload(event: BeforeUnloadEvent): void {
  if (!generations.uploading) return
  event.preventDefault()
  event.returnValue = ''
}

useModal(computed(() => Boolean(generations.viewer)), generations.closeViewer)

watch(
  () => auth.identity?.user_id,
  (userId) => {
    generations.reset()
    if (userId) void generations.restore()
  },
  { immediate: true },
)

onMounted(() => {
  void app.loadPublicData()
  window.addEventListener('beforeunload', guardUpload)
})
onBeforeUnmount(() => window.removeEventListener('beforeunload', guardUpload))
</script>

<template>
  <OnboardingFrame
    v-if="initialPlans"
    phase="plans"
    plans
  >
    <RouterView />
  </OnboardingFrame>
  <RouterView v-else-if="bareLayout" />
  <div
    v-else
    class="app-frame"
  >
    <div
      class="app-shell"
      :class="{ 'app-shell--focused': !showChrome, 'app-shell--composer': route.name === 'create', 'app-shell--welcome': showChrome && app.publicConfig.welcome_bonus_enabled && auth.isAuthenticated && welcomeBonus.active }"
    >
      <aside
        v-if="showChrome"
        class="side-nav"
        aria-label="Primary navigation"
      >
        <img
          class="side-nav__logo"
          src="/assets/logo/logo.svg"
          alt="UIG"
        >
        <RouterLink
          v-for="tab in tabs"
          :key="tab.to"
          :to="tab.to"
          class="side-nav__item"
        >
          <UigIcon
            :name="tab.icon"
            :size="21"
          />
          <span>{{ tab.label }}</span>
        </RouterLink>
        <GenerationIndicator />
        <AccountChip class="side-nav__account" />
      </aside>

      <header
        v-if="showChrome"
        class="top-bar"
      >
        <img
          class="top-bar__logo"
          src="/assets/logo/logo.svg"
          alt="UIG"
        >
        <GenerationIndicator />
        <AccountChip compact />
      </header>

      <main class="app-content">
        <RouterView v-slot="{ Component, route: activeRoute }">
          <KeepAlive
            include="StudioView"
            :max="1"
          >
            <component
              :is="Component"
              :key="activeRoute.name === 'studio' ? 'studio' : activeRoute.fullPath"
            />
          </KeepAlive>
        </RouterView>
        <AppFooter v-if="route.meta.footer !== false" />
      </main>

      <nav
        v-if="showChrome"
        class="bottom-nav"
        aria-label="Primary navigation"
      >
        <RouterLink
          v-for="tab in tabs"
          :key="tab.to"
          :to="tab.to"
          class="nav-item"
        >
          <UigIcon
            :name="tab.icon"
            :size="20"
          />
          <span>{{ tab.label }}</span>
        </RouterLink>
      </nav>
    </div>
  </div>
  <GenerationToasts />
  <Teleport to="body">
    <Transition name="result-reveal">
      <GenerationOverlay
        v-if="generations.viewer"
        :generation="generations.viewer"
        @close="generations.closeViewer"
      />
    </Transition>
  </Teleport>
  <AuthModal />
  <WelcomeBonus />
</template>
